import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader, SkeletonBlock, EmptyState } from '../../../components/module-ui';
import {
  fetchEntry, setActiveEasychitConnectionId, setActiveGlobalSchema, setAuditUserId,
} from '../api/connectionRegistryApi';
import { useAuth } from '../../../App';
import { MODULES } from '../moduleRegistry';
import ModuleTypeNav from '../components/ModuleTypeNav';
import MakeItLiveButton from '../components/MakeItLiveButton';
import { useAllowedScreens } from '../utils/screenAccess';
import { useSupportingTools } from '../SupportingToolsContext';

function ClientWorkspacePage() {
  const { connectionId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { basePath } = useSupportingTools();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedModule, setSelectedModule] = useState(null);
  const { canOpen } = useAllowedScreens();

  useEffect(() => {
    setActiveEasychitConnectionId(connectionId);
    return () => {
      setActiveEasychitConnectionId(null);
      setActiveGlobalSchema(null);
    };
  }, [connectionId]);

  // The EasyChit *_log tables stamp each change with this operator's contact_id.
  useEffect(() => {
    setAuditUserId(user?.easychit_contact_id);
    return () => setAuditUserId(null);
  }, [user]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const loaded = await fetchEntry(connectionId);
        setEntry(loaded);
        // Screens whose endpoint takes globalSchema as a query param read it
        // from here rather than assuming the default "GLOBAL" schema.
        setActiveGlobalSchema(loaded?.global_schema);
      } catch (e) {
        console.error('fetchEntry', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [connectionId]);

  if (loading) {
    return <SkeletonBlock rows={6} />;
  }

  if (!entry) {
    return (
      <EmptyState
        title="Connection not found"
        description="This client connection may have been removed."
        action={(
          <button type="button" className="tk-btn tk-btn-secondary" onClick={() => navigate(`${basePath}/registry`)}>
            Back to Registry
          </button>
        )}
      />
    );
  }

  // The nav only offers permitted screens, but the selection is held in state:
  // re-check the right here so a revoked screen stops rendering immediately.
  const selected = MODULES.find((m) => m.key === selectedModule && canOpen(m.key));

  return (
    <div data-testid="client-workspace-page">
      <PageHeader
        title={entry.client_name}
        subtitle={entry.product_label}
      />

      <div style={{ display: 'grid', gridTemplateColumns: '260px minmax(0, 1fr)', gap: '20px', alignItems: 'start' }}>
        <ModuleTypeNav
          heading="Modules"
          selectedKey={selectedModule}
          onSelect={setSelectedModule}
          testIdPrefix="module"
        />
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
              {selected ? selected.label : 'Change Request'}
            </div>
            <MakeItLiveButton screen={selected} />
          </div>
          {!selected && (
            <div
              style={{ padding: '40px 24px', textAlign: 'center', background: 'white', border: '1px dashed #E5E7EB', borderRadius: '8px', color: '#9CA3AF', fontSize: '14px' }}
              data-testid="change-request-empty"
            >
              Pick a type on the left, then a screen inside it, to start a change request.
            </div>
          )}
          {selected && (
            // A screen wider than the pane scrolls sideways here, with the bar under the screen.
            <div style={{ overflowX: 'auto' }} data-testid="screen-scroll">
              <selected.Component connectionId={connectionId} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ClientWorkspacePage;
